import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ElementRef,
} from "@angular/core";
import {
  ChatContact,
  ChatMsg,
  SendMsgEventData,
  ChatTask,
} from "src/app/shared/models/chat.model";
import { BroshService } from "src/app/shared/services/brosh.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { ChatService } from "src/app/shared/services/chat.service";
import { Order, OrderFile } from "src/app/shared/models/order.model";
import { TranslateService } from "@ngx-translate/core";
import { ChatComponent } from "src/app/shared/components/chat/chat.component";
import { map, switchMap, takeUntil, tap } from "rxjs/operators";
import { Actions, ofType } from "@ngrx/effects";
import { Subject } from "rxjs";
import {
  CREATED_CHAT_TASK,
  REQUEST_DASHBOARD_REFRESH,
  SEND_FILE_CHAT_MSG,
} from "src/app/shared/ngrx/ngrx-actions";
import { Store } from "@ngrx/store";
import { SET_DISPLAYED_FILE, CLEAR_DISPLAYED_FILE } from "src/app/shared/ngrx/ngrx-actions";
import { currentDisplayedFileId } from "src/app/shared/ngrx/ngrx-selectors";
import { CommonModule } from "@angular/common";
import { ModalPopupComponent } from "src/app/shared/components/ui/modal-popup.component";
import { FilePreviewComponent } from "../file-preview/file-preview.component";
import { DialogModule } from "primeng/dialog";

@Component({
  selector: "order-chat",
  templateUrl: "./order-chat.component.html",
  styleUrls: ["./order-chat.component.sass"],

  imports: [
    ChatComponent,
    CommonModule,
    DialogModule,
    FilePreviewComponent,
  ],
  standalone: true,
})
export class OrderChatComponent {
  @Input() contact: ChatContact;
  @Input() order: Order;
  @Input() disabled: boolean = false;
  @Output() onCreateTask = new EventEmitter<ChatMsg>();
  @Output() onFirstMessagesLoaded = new EventEmitter<void>();

  @ViewChild(ChatComponent) chatComponent: ChatComponent;

  refreshChatTimer: any;
  changeSchedChatTimer: any;
  refreshChatIntervalSeconds: number = 60 * 2;
  refreshChatHighIntervalSeconds: number = 15;
  refreshOnReattach: boolean = false;
  filePopupDisplay: boolean;
  selectedFile: OrderFile;
  lastChatStatDate: number;
  destroy$ = new Subject();
  private isFirstMessagesLoad: boolean = true;
  currentDisplayedFileId = currentDisplayedFileId;

  get unreadMsgs(): ChatMsg[] {
    if (!this.contact.messages) return [];
    return this.contact.messages.filter(
      (m) => !m.readByMe && !m.unreadDividerSw
    );
  }

  get forceLtr(): boolean {
    return this.contact.contactId == "PLANT";
  }

  constructor(
    public readonly store: Store,
    private sharedService: SharedService,
    private chatService: ChatService,
    private translateService: TranslateService,
    private actions$: Actions,
    private elRef: ElementRef
  ) { }

  ngAfterViewInit(): void {
    this.getMessages(null, true);
    this.scheduleRefreshChat(this.refreshChatIntervalSeconds);

    this.actions$
      .pipe(
        ofType(CREATED_CHAT_TASK),
        takeUntil(this.destroy$),
        map(({ data }) => {
          if (this.contact.messages) {
            let msg = this.contact.messages.find((m) => m.msgId == data.msgId);
            if (msg) {
              msg.taskId = 1;
            }
          }
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(SEND_FILE_CHAT_MSG),
        takeUntil(this.destroy$),
        map(({ chatContact, orderFile }) => {
          if (chatContact == this.contact) {
            this.sendFileMsg(orderFile);
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.clearRefreshTimer();
    this.destroy$.next(1);

    if (this.changeSchedChatTimer) {
      clearInterval(this.changeSchedChatTimer);
    }
  }

  scheduleRefreshChat(intervalSec: number) {
    this.clearRefreshTimer();

    this.refreshChatTimer = setInterval(() => {
      // while detached by the route-reuse cache, defer polling —
      // ngDoCheck refreshes as soon as the component is re-attached
      if (!document.contains(this.elRef.nativeElement)) {
        this.refreshOnReattach = true;
        return;
      }
      this.pollChatStat();
    }, intervalSec * 1000);
  }

  // ngDoCheck doesn't run while the component is detached from the CD tree,
  // so the first call after a deferred poll marks the re-attach moment
  ngDoCheck() {
    if (this.refreshOnReattach) {
      this.refreshOnReattach = false;
      this.pollChatStat();
    }
  }

  pollChatStat() {
    this.chatService.getChatStat(this.order.clinicId).subscribe((data) => {
      if (
        (data != null && this.lastChatStatDate == null) ||
        (data != null &&
          this.lastChatStatDate != null &&
          data > this.lastChatStatDate)
      ) {
        this.getMessages(null, true);
      }
      this.lastChatStatDate = data;
    });
  }

  clearRefreshTimer() {
    if (this.refreshChatTimer) {
      clearInterval(this.refreshChatTimer);
    }
  }

  getMessages(callback: any = null, scrollToBottom: boolean = false) {
    this.chatService.getOrderChatMessages(this.order, this.contact).subscribe(
      (data) => {
        let chatMsgs = [];
        let unreadPlaceholder = new ChatMsg();
        unreadPlaceholder.unreadDividerSw = true;

        data.forEach((m) => {
          if (!m.readByMe && !chatMsgs.includes(unreadPlaceholder)) {
            chatMsgs.push(unreadPlaceholder);
          }
          chatMsgs.push(m);
        });

        let refreshChat = true;
        if (this.contact.messages) {
          let last = chatMsgs[chatMsgs.length - 1];
          if (
            last &&
            this.contact.messages.find((m) => m.msgId == last.msgId)
          ) {
            refreshChat = false;
          }

          if (
            this.contact.messages.find(
              (m) => !chatMsgs.find((m1) => m1.msgId == m.msgId)
            )
          ) {
            refreshChat = true;
          }
        }

        if (refreshChat) {
          this.contact.messages = chatMsgs;

          if (scrollToBottom && this.chatComponent) {
            setTimeout(() => {
              this.chatComponent.scrollToBottom();
            }, 10);
          }

          if (this.unreadMsgs.length > 0) {
            setTimeout(() => {
              this.markRead(this.unreadMsgs);
            }, 1500);
          }

          this.chatComponent.initAudio();
        }

        // Emit event after first successful messages load
        if (this.isFirstMessagesLoad) {
          this.onFirstMessagesLoaded.emit();
          this.isFirstMessagesLoad = false;
        }

        if (callback) callback();
      },
      () => {
        if (callback) callback();
      }
    );
  }

  onSendMsg(event: SendMsgEventData) {
    this.chatService.sendChatMsg(this.order, this.contact, event.msg).subscribe(
      (data) => {
        event.onSuccessCallback();
        this.store.dispatch(REQUEST_DASHBOARD_REFRESH());
        this.chatService.updChatStat(this.order.clinicId).subscribe();

        this.scheduleRefreshAfterMessage();
      },
      (error) => {
        event.onErrorCallback();
        this.sharedService.alertError(error);
      }
    );
  }

  sendFileMsg(file: OrderFile) {
    let msg = new ChatMsg();
    msg.fileId = file.fileId;
    this.chatService.sendChatMsg(this.order, this.contact, msg).subscribe(
      (data) => {
        this.sharedService.alertSuccess(
          this.translateService.instant("translate.chat.fileSent")
        );
        this.store.dispatch(REQUEST_DASHBOARD_REFRESH());
        this.chatService.updChatStat(this.order.clinicId).subscribe();

        this.scheduleRefreshAfterMessage();
        this.getMessages();
      },
      (error) => {
        this.sharedService.alertError(error);
      }
    );
  }

  scheduleRefreshAfterMessage() {
    this.scheduleRefreshChat(this.refreshChatHighIntervalSeconds);
    if (this.changeSchedChatTimer) {
      clearInterval(this.changeSchedChatTimer);
    }
    this.changeSchedChatTimer = setTimeout(() => {
      this.scheduleRefreshChat(this.refreshChatIntervalSeconds);
    }, 5 * 60 * 1000);
  }

  onChatRefresh(callback: any) {
    this.getMessages(callback);
  }

  markRead(msgList: ChatMsg[]) {
    this.chatService.markChatMsgRead(this.order, msgList).subscribe(
      (data) => {
        this.store.dispatch(REQUEST_DASHBOARD_REFRESH());
      },
      (error) => {
        this.sharedService.alertError(error);
      }
    );
  }

  onFileMsgClicked(msg: ChatMsg) {
    const displayedFileId = this.store.selectSignal(currentDisplayedFileId)();

    // If another file is displayed, close it first
    if (displayedFileId) {
      this.store.dispatch(CLEAR_DISPLAYED_FILE());
      setTimeout(() => {
        this.onFileMsgClicked(msg);
      }, 200);
      return;
    }

    this.selectedFile = msg.file;
    this.filePopupDisplay = true;
    this.store.dispatch(SET_DISPLAYED_FILE({ fileId: msg.file.fileId }));
  }

  onDeleteMsgClicked(msg: ChatMsg) {
    this.chatService.deleteChatMsg(msg, this.order).subscribe((data) => {
      this.chatService.updChatStat(this.order.clinicId).subscribe();

      this.contact.messages.splice(this.contact.messages.indexOf(msg), 1);
    });
  }

  onCreateTaskClicked(msg: ChatMsg) {
    this.onCreateTask.emit(msg);
  }
}
